/**
 * Shell do editor de Playbook: pagehead + tablist das 9 abas + corpo da aba.
 *
 * Leitura (PR1/PR2) vs edição (PR4): o gate `playbook-author` decide. SEM a
 * capability, a tela é idêntica ao PR2 (read-only, sem barra de ações) —
 * defense-in-depth: o gate está na AÇÃO (mutation + render), não só no botão.
 * COM a capability, cada aba `ready` vira editável, com barra de ações
 * (Salvar/Descartar) e tratamento de conflito 409 (não sobrescreve cego).
 * Verificação segue honest-defer (sem campo no backend); Gatilhos é editável
 * desde a ADR-148 Fatia 3 (aba 4).
 */

import { useQueryClient } from '@tanstack/react-query';
import { ChevronLeft } from 'lucide-react';
import { useState } from 'react';

import { useCapability } from '../../hooks/use-capability';
import { ApiError } from '../../lib/api-client';
import { usePersonaList } from '../personas/use-personas';

import { PlaybookActionBar } from './PlaybookActionBar';
import { PlaybookStatusPill } from './PlaybookStatusPill';
import { PLAYBOOK_TABS } from './tab-definitions';
import { TabAppetite } from './tabs/TabAppetite';
import { TabAppetiteEdit } from './tabs/TabAppetiteEdit';
import { TabAutoClose } from './tabs/TabAutoClose';
import { TabAutoCloseEdit } from './tabs/TabAutoCloseEdit';
import { TabChecklist } from './tabs/TabChecklist';
import { TabChecklistEdit } from './tabs/TabChecklistEdit';
import { TabConduct, TabConductEdit } from './tabs/TabConduct';
import { TabGeneralEdit } from './tabs/TabGeneralEdit';
import { TabGeneralRead } from './tabs/TabGeneralRead';
import { TabOperation } from './tabs/TabOperation';
import { TabOperationEdit } from './tabs/TabOperationEdit';
import { TabPlaceholder } from './tabs/TabPlaceholder';
import { TabQuickReplies } from './tabs/TabQuickReplies';
import { TabQuickRepliesEdit } from './tabs/TabQuickRepliesEdit';
import { TabSteps } from './tabs/TabSteps';
import { TabStepsEdit } from './tabs/TabStepsEdit';
import { TabTriggers, TabTriggersEdit } from './tabs/TabTriggers';
import { TabVerification, TabVerificationEdit } from './tabs/TabVerification';
import { usePlaybookForm } from './use-playbook-form';
import { useUpdatePlaybook } from './use-playbooks';

import type { PlaybookTabDef, PlaybookTabKey } from './tab-definitions';
import type { PersonaOption } from './tabs/TabGeneralEdit';
import type { PlaybookDto } from './types';
import type { PlaybookForm } from './use-playbook-form';

function renderReadTab(def: PlaybookTabDef, playbook: PlaybookDto): JSX.Element {
  switch (def.key) {
    case 'general':
      return <TabGeneralRead playbook={playbook} />;
    case 'steps':
      return <TabSteps playbook={playbook} />;
    case 'operation':
      return <TabOperation playbook={playbook} />;
    case 'appetite':
      return <TabAppetite playbook={playbook} />;
    case 'quickreplies':
      return <TabQuickReplies playbook={playbook} />;
    case 'autocloseable':
      return <TabAutoClose playbook={playbook} />;
    case 'checklist':
      return <TabChecklist playbook={playbook} />;
    case 'triggers':
      // ADR-148 Fatia 3 — saiu do honest-defer.
      return <TabTriggers playbook={playbook} />;
    case 'verification':
      // ADR-149 Fatia 2 — saiu do honest-defer.
      return <TabVerification playbook={playbook} />;
    case 'conduct':
      // Fase 2 «Conduta e Contexto» — nível PLAYBOOK da cascata.
      return <TabConduct playbook={playbook} />;
    default:
      return <TabPlaceholder tab={def} />;
  }
}

function renderEditTab(
  def: PlaybookTabDef,
  playbook: PlaybookDto,
  form: PlaybookForm,
  personas: readonly PersonaOption[],
): JSX.Element {
  switch (def.key) {
    case 'general':
      return <TabGeneralEdit form={form} playbook={playbook} personas={personas} />;
    case 'steps':
      return <TabStepsEdit form={form} />;
    case 'operation':
      return <TabOperationEdit form={form} playbook={playbook} />;
    case 'appetite':
      return <TabAppetiteEdit form={form} />;
    case 'quickreplies':
      return <TabQuickRepliesEdit form={form} />;
    case 'autocloseable':
      return <TabAutoCloseEdit form={form} playbook={playbook} />;
    case 'checklist':
      return <TabChecklistEdit form={form} />;
    case 'triggers':
      // ADR-148 Fatia 3 — saiu do honest-defer.
      return <TabTriggersEdit form={form} />;
    case 'verification':
      // ADR-149 Fatia 2 — saiu do honest-defer.
      return <TabVerificationEdit form={form} />;
    case 'conduct':
      // Fase 2 «Conduta e Contexto» — nível PLAYBOOK da cascata.
      return <TabConductEdit playbook={playbook} form={form} />;
    default:
      return <TabPlaceholder tab={def} />;
  }
}

export function PlaybookEditorView({
  playbook,
  onBack,
}: {
  playbook: PlaybookDto;
  onBack: () => void;
}): JSX.Element {
  const canAuthor = useCapability('playbook-author');
  // Fase 0b — a voz da IA chega ao motor SÓ por `Playbook.personaId`. O hook é
  // gateado em `persona-read` e devolve [] sem a cap (ADR-132 capability-driven UI):
  // quem não pode ler personas vê o seletor vazio, não um erro.
  const { data: personas } = usePersonaList();
  // ADR-149 — aba 6 só existe para tenant com o motor de verificação (Team+;
  // mockup: Solo não vê a aba). Filtra a tablist E o corpo (defense-in-depth).
  const canVerification = useCapability('proactive-verification');
  const visibleTabs = PLAYBOOK_TABS.filter((t) => t.key !== 'verification' || canVerification);
  const [activeTab, setActiveTab] = useState<PlaybookTabKey>('general');
  const activeDef = visibleTabs.find((t) => t.key === activeTab) ?? visibleTabs[0]!;

  const queryClient = useQueryClient();
  const form = usePlaybookForm(playbook);
  const mutation = useUpdatePlaybook(playbook.id);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [conflict, setConflict] = useState<{ currentVersion: number | null } | null>(null);

  const handleSave = (): void => {
    const patch = form.buildPatch();
    if (patch === null) return;
    setErrorMessage(null);
    mutation.mutate(
      { expectedVersion: playbook.version, body: patch },
      {
        onError: (err) => {
          if (err instanceof ApiError && err.status === 409) {
            const body = err.body as { currentVersion?: number };
            setConflict({ currentVersion: body.currentVersion ?? null });
            return;
          }
          setErrorMessage('Não foi possível salvar. Tente novamente.');
        },
        onSuccess: () => {
          setConflict(null);
          setErrorMessage(null);
        },
      },
    );
  };

  const handleDiscard = (): void => {
    form.reset();
    setErrorMessage(null);
    setConflict(null);
  };

  const handleReloadConflict = (): void => {
    setConflict(null);
    setErrorMessage(null);
    void queryClient.invalidateQueries({ queryKey: ['playbook-detail', playbook.id] });
  };

  return (
    <div className="playbooks-screen">
      <div className="pb-pagehead">
        <button type="button" className="pb-btn pb-btn--ghost" onClick={onBack}>
          <ChevronLeft size={14} /> Voltar
        </button>
        <div className="pb-pagehead__heading">
          <div className="pb-pagehead__crumbs">Playbooks · Editor</div>
          <div className="pb-pagehead__title">
            <span className="pb-pagehead__name">“{playbook.name}”</span>
            <PlaybookStatusPill status={playbook.status} />
          </div>
          <div className="pb-pagehead__sub">
            v{playbook.version} · {canAuthor ? 'edição' : 'somente leitura'}
          </div>
        </div>
      </div>

      {canAuthor && conflict !== null && (
        <div className="pb-conflict-banner" role="alert">
          <span>
            Este playbook mudou desde que você abriu
            {conflict.currentVersion !== null
              ? ` (versão atual: v${conflict.currentVersion.toString()})`
              : ''}
            . Recarregue para ver a versão mais recente antes de editar — suas alterações não foram
            salvas.
          </span>
          <button type="button" className="pb-btn pb-btn--sm" onClick={handleReloadConflict}>
            Recarregar
          </button>
        </div>
      )}

      <div className="pb-editor-layout pb-editor-layout--no-side">
        <div className="pb-editor-main">
          <div className="pb-tablist" role="tablist" aria-label="Seções do playbook">
            {visibleTabs.map((tab) => (
              <button
                key={tab.key}
                type="button"
                role="tab"
                aria-selected={activeTab === tab.key}
                className={'pb-tab' + (activeTab === tab.key ? ' pb-tab--active' : '')}
                onClick={() => {
                  setActiveTab(tab.key);
                }}
              >
                <span className="pb-tab__num">{tab.id}.</span>
                <span>{tab.label}</span>
              </button>
            ))}
          </div>

          {canAuthor
            ? renderEditTab(activeDef, playbook, form, personas)
            : renderReadTab(activeDef, playbook)}
        </div>
      </div>

      {canAuthor && (
        <PlaybookActionBar
          isDirty={form.isDirty}
          isSaving={mutation.isPending}
          errorMessage={errorMessage}
          onSave={handleSave}
          onDiscard={handleDiscard}
        />
      )}
    </div>
  );
}
